import React, { useRef, useState, useEffect } from "react";

interface Testimonial {
  rating: number;
  quote: string;
  author: string;
  meta: string;
}

interface TestimonialsSectionProps {
  eyebrow?: string;
  title?: string;
  testimonials?: Testimonial[];
}

const defaultTestimonials: Testimonial[] = [
  {
    rating: 5,
    quote:
      "Ahmad Ba prend vraiment le temps d'expliquer avant de commencer. J'ai apprécié son sérieux et sa pédagogie. Je me suis sentie en confiance du début à la fin.",
    author: "Mariama S.",
    meta: "Hijama · Mars 2025",
  },
  {
    rating: 5,
    quote:
      "Les produits sont bien sélectionnés et les conseils sont précis. On n'est pas dans le flou — chaque produit est expliqué avec ses précautions.",
    author: "Abdoulaye D.",
    meta: "Phytothérapie · Janvier 2025",
  },
  {
    rating: 5,
    quote:
      "Une approche sérieuse, sans exagérations. Fouta Bio présente les choses clairement, avec des références vérifiées. C'est rassurant.",
    author: "F. N.",
    meta: "Médecine prophétique · Février 2025",
  },
];

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="testimonial-stars mb-3" aria-label={`${rating} sur 5 étoiles`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? "star star-filled" : "star"}>
          ★
        </span>
      ))}
    </div>
  );
};

const Testimonial: React.FC<TestimonialsSectionProps> = ({
  eyebrow = "Avis",
  title = "La confiance de celles et ceux que nous accompagnons",
  testimonials = defaultTestimonials,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Met à jour le point actif selon la position de défilement (mobile).
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(trackCenter - slideCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToIndex = (index: number) => {
    const slide = slideRefs.current[index];
    slide?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  return (
    <section className="testimonials-section py-5 py-lg-6">
      <div className="container-xl">
        <div className="text-center mb-5">
          <p className="testimonials-eyebrow mb-3">{eyebrow}</p>
          <h2 className="testimonials-title">{title}</h2>
        </div>

        <div className="testimonials-track" ref={trackRef}>
          {testimonials.map((t, index) => (
            <div
              key={index}
              className="testimonial-slide"
              ref={(el) => (slideRefs.current[index] = el)}
            >
              <div className="testimonial-card h-100 p-4">
                <StarRating rating={t.rating} />
                <p className="testimonial-quote mb-4">"{t.quote}"</p>
                <hr className="testimonial-divider" />
                <p className="testimonial-author mb-0">{t.author}</p>
                <p className="testimonial-meta mb-0">{t.meta}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Points de pagination — visibles uniquement sur mobile */}
        <div className="testimonials-dots d-flex d-md-none justify-content-center gap-2 mt-4">
          {testimonials.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Aller à l'avis ${index + 1}`}
              className={`testimonial-dot ${
                index === activeIndex ? "testimonial-dot-active" : ""
              }`}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonial;